/* eslint-disable no-param-reassign */
import { CohortHierarchyDTO } from '@atlassiansox/phobos-client-ts';
import cytoscape from 'cytoscape';

import { SPACE_COMFORTABLE } from '@/styles';

import {
  getAnchorIDFromCohortType,
  nodeBackgroundColor,
  nodeBackgroundColorHover,
} from './constants';

export const setupCytoscapeInstance = (
  cyInstance: cytoscape.Core,
  containerRef: HTMLDivElement
): void => {
  cyInstance.zoom(1);

  cyInstance.on('mouseover', 'node', event => {
    containerRef.style.cursor = 'pointer';
    event.target.style({ 'background-color': nodeBackgroundColorHover });
  });

  cyInstance.on('mouseout', 'node', event => {
    containerRef.style.cursor = 'default';
    event.target.style({ 'background-color': nodeBackgroundColor });
  });

  cyInstance.on('tap', 'node', event => {
    const nodeData = event.target.data();
    const element = document.querySelector(`#${getAnchorIDFromCohortType(nodeData.id)}`);
    element?.scrollIntoView({ behavior: 'smooth' });
    event.target.style({ 'background-color': nodeBackgroundColor });
  });

  // Get the height of the rendered graph
  const extent = cyInstance.elements().boundingBox();
  const graphHeight = extent.h;
  const currentPan = cyInstance.pan();
  cyInstance.pan({
    x: currentPan.x,
    // Add 30px at the top so the text labels are not cut off
    y: currentPan.y + 30,
  });

  // Need some extra space (SPACE_COMFORTABLE / 2) at the top and bottom so
  // when mouse is near the edge, it doesn't cut off the mouseover and mouseout events
  containerRef.style.height = `${graphHeight + SPACE_COMFORTABLE}px`;

  // Need to resize and center again after changing container size
  cyInstance.resize();
  cyInstance.center();
};

export const cohortHierarchyToBFSData = (cohortHierarchy: CohortHierarchyDTO): string[] => {
  if (!cohortHierarchy) return [];

  const result: string[] = [];
  const queue: CohortHierarchyDTO[] = [cohortHierarchy];

  while (queue.length > 0) {
    const current = queue.shift()!;
    result.push(current.cohort);

    // Add all children to the queue for next level processing
    if (current.children && current.children.length > 0) {
      queue.push(...current.children);
    }
  }

  return result;
};

export const transformCohortHierarchy = (
  cohortData: any[],
  parentId: string | null = null
): { nodes: any[]; edges: any[] } => {
  const nodes: any[] = [];
  const edges: any[] = [];

  cohortData.forEach(item => {
    const nodeId = item.cohort;

    // Add current node
    nodes.push({
      data: { id: nodeId, label: nodeId.toUpperCase() },
    });

    // Add edge from parent to current node
    if (parentId) {
      edges.push({
        data: { source: parentId, target: nodeId },
      });
    }

    // Process children recursively
    if (item.children && item.children.length > 0) {
      const childData = transformCohortHierarchy(item.children, nodeId);
      nodes.push(...childData.nodes);
      edges.push(...childData.edges);
    }
  });

  return { nodes, edges };
};

export const createCytoscapeData = (cohortHierarchy: CohortHierarchyDTO | undefined) => {
  // Use actual cohortHierarchy data if available
  if (cohortHierarchy) {
    return transformCohortHierarchy([cohortHierarchy]);
  }

  return { nodes: [], edges: [] };
};
